import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Apple, Film, Sparkles, Play } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HomePage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  
  const heroSlides = [
    {
      image: 'https://images.unsplash.com/photo-1490818387583-1baba5e638af?w=1600',
      brand: 'Nutrition',
      tagline: 'Avail The Best You.'
    },
    {
      image: 'https://images.unsplash.com/photo-1492691527719-9d1e07e534b4?w=1600',
      brand: 'Media',
      tagline: 'See Your Dream.'
    },
    {
      image: 'https://images.unsplash.com/photo-1763906472972-b97c54199e14?w=1600',
      brand: 'Fashion',
      tagline: 'Own Your Uniqueness.'
    }
  ];

  useEffect(() => {
    // Seed data on first load
    axios.post(`${API}/seed`).catch(() => {});
    
    // Auto-advance slideshow every 5 seconds
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
    }, 5000);
    
    return () => clearInterval(interval);
  }, []);

  const brands = [
    {
      name: 'Nutrition',
      tagline: 'Nourish Your Life',
      description: 'Personalized dietary care and nutrition plans tailored to your health goals and preferences.',
      image: 'https://images.pexels.com/photos/6728515/pexels-photo-6728515.jpeg',
      path: '/nutrition',
      icon: Apple,
      color: '#E5D3B3'
    },
    {
      name: 'Media',
      tagline: 'Cinematic Excellence',
      description: 'From filmmaking to branding, we bring your creative vision to life with unmatched artistry.',
      image: 'https://images.pexels.com/photos/3379943/pexels-photo-3379943.jpeg',
      path: '/media',
      icon: Film,
      color: '#FAD6C6'
    },
    {
      name: 'Fashion',
      tagline: 'Handcrafted Luxury',
      description: 'Bespoke fashion pieces meticulously crafted to elevate your confidence and personal style.',
      image: 'https://images.unsplash.com/photo-1760726433630-904f1a4f46ab?w=800',
      path: '/fashion',
      icon: Sparkles,
      color: '#8C7B6B'
    }
  ];

  return (
    <div className="min-h-screen bg-[#0A0A0A]">
      {/* Hero Section with Animated Image Slideshow */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Animated Image Slideshow */}
        <div className="absolute inset-0">
          <AnimatePresence mode="wait">
            <motion.img
              key={currentSlide}
              src={heroSlides[currentSlide].image}
              alt={heroSlides[currentSlide].brand}
              className="w-full h-full object-cover absolute inset-0"
              initial={{ opacity: 0, scale: 1.1 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 1.5, ease: "easeInOut" }}
            />
          </AnimatePresence>
          <div className="hero-overlay absolute inset-0" />
        </div>
        
        {/* Content */}
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-sm tracking-[0.3em] uppercase text-[#FFCBA4] mb-6">
              Welcome to
            </p>
            <img 
              src="https://customer-assets.emergentagent.com/job_build-site-461/artifacts/if5761vx_Arteryvail-New-Logo-removebg-preview.png" 
              alt="ARTERYVAIL" 
              className="h-24 md:h-32 lg:h-40 w-auto mx-auto mb-4"
            />
            <p className="font-serif text-xl md:text-2xl text-[#5C3D2E] italic mb-4">
              Welcome. You're home now.
            </p>
            
            {/* Current Slide Brand Tagline */}
            <AnimatePresence mode="wait">
              <motion.p
                key={currentSlide}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.6, delay: 0.8 }}
                className="text-lg text-[#FFCBA4] tracking-widest uppercase mb-8"
              >
                {heroSlides[currentSlide].tagline}
              </motion.p>
            </AnimatePresence>
            
            <p className="text-lg md:text-xl text-[#A3988F] max-w-2xl mx-auto leading-relaxed mb-12">
              Three worlds of excellence. One destination for those who demand the extraordinary.
            </p>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.6 }}
            >
              <a href="#brands" className="btn-primary inline-flex items-center space-x-3" data-testid="hero-explore-btn">
                <span>Explore Our World</span>
                <ArrowRight className="w-4 h-4" />
              </a>
            </motion.div>
          </motion.div>
        </div>

        {/* Slide Indicators */}
        <div className="absolute bottom-24 left-1/2 -translate-x-1/2 flex space-x-3 z-20">
          {heroSlides.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentSlide(index)}
              className={`w-2 h-2 rounded-full transition-all duration-300 ${
                index === currentSlide 
                  ? 'bg-[#FFCBA4] w-8' 
                  : 'bg-[#A3988F]/50 hover:bg-[#A3988F]'
              }`}
              aria-label={`Go to ${heroSlides[index].brand} slide`}
            />
          ))}
        </div>

        {/* Scroll Indicator */}
        <motion.div
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-6 h-10 border-2 border-[#E5D3B3]/30 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-[#E5D3B3] rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* Brands Section */}
      <section id="brands" className="section-padding bg-gradient-to-b from-[#0A0A0A] to-[#1A1514]">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-20"
          >
            <p className="text-sm tracking-[0.3em] uppercase text-[#E5D3B3] mb-4">Our Brands</p>
            <h2 className="font-serif text-4xl md:text-6xl text-[#F5F2EB]">Three Pillars of Excellence</h2>
          </motion.div>

          {/* Bento Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {brands.map((brand, index) => (
              <motion.div
                key={brand.name}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className={`brand-card relative rounded-2xl overflow-hidden ${
                  index === 0 ? 'lg:row-span-2 h-[500px] lg:h-auto' : 'h-[400px]'
                }`}
              >
                <img
                  src={brand.image}
                  alt={brand.name}
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 z-10 flex flex-col justify-end p-8 lg:p-10">
                  <div className="flex items-center space-x-3 mb-4">
                    <brand.icon className="w-5 h-5" style={{ color: brand.color }} />
                    <span className="text-xs tracking-[0.2em] uppercase" style={{ color: brand.color }}>
                      {brand.tagline}
                    </span>
                  </div>
                  <h3 className="font-serif text-3xl lg:text-4xl text-[#F5F2EB] mb-3">
                    {brand.name}
                  </h3>
                  <p className="text-[#A3988F] text-sm leading-relaxed mb-6 max-w-md">
                    {brand.description}
                  </p>
                  <Link
                    to={brand.path}
                    className="inline-flex items-center space-x-2 text-sm tracking-widest uppercase text-[#F5F2EB] hover:text-[#E5D3B3] transition-colors"
                    data-testid={`brand-${brand.name.toLowerCase()}-link`}
                  >
                    <span>Discover</span>
                    <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="section-padding bg-[#1A1514]">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <p className="text-sm tracking-[0.3em] uppercase text-[#E5D3B3] mb-6">Our Philosophy</p>
            <h2 className="font-serif text-3xl md:text-5xl text-[#F5F2EB] leading-tight mb-8">
              "Excellence is not a destination, it's a continuous journey of refinement and artistry."
            </h2>
            <p className="text-[#A3988F] text-lg leading-relaxed">
              At ARTERYVAIL, we believe in the transformative power of personalized care. Whether nurturing your health, 
              telling your story through media, or crafting your signature style, we approach every endeavor with 
              unwavering dedication to excellence.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Gallery Section */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <p className="text-sm tracking-[0.3em] uppercase text-[#5C3D2E] mb-4">Gallery</p>
            <h2 className="font-serif text-4xl md:text-5xl text-[#F5F2EB]">Moments of Excellence</h2>
          </motion.div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              'https://images.pexels.com/photos/1640774/pexels-photo-1640774.jpeg',
              'https://images.pexels.com/photos/3062541/pexels-photo-3062541.jpeg',
              'https://images.pexels.com/photos/1536619/pexels-photo-1536619.jpeg',
              'https://images.pexels.com/photos/2873486/pexels-photo-2873486.jpeg',
              'https://images.pexels.com/photos/3768894/pexels-photo-3768894.jpeg',
              'https://images.pexels.com/photos/1537671/pexels-photo-1537671.jpeg',
              'https://images.pexels.com/photos/3622614/pexels-photo-3622614.jpeg',
              'https://images.pexels.com/photos/5632397/pexels-photo-5632397.jpeg',
            ].map((img, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className={`relative overflow-hidden rounded-xl ${index === 0 || index === 5 ? 'row-span-2' : ''}`}
              >
                <img
                  src={img}
                  alt={`Gallery ${index + 1}`}
                  className="w-full h-full object-cover aspect-square hover:scale-105 transition-transform duration-500"
                />
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section-padding">
        <div className="max-w-7xl mx-auto">
          <div className="relative rounded-2xl overflow-hidden bg-[#3D2314]">
            <img
              src="https://images.pexels.com/photos/6839656/pexels-photo-6839656.jpeg"
              alt="Contact ARTERYVAIL"
              className="w-full h-[400px] md:h-[500px] object-cover opacity-40"
            />
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center px-6 max-w-2xl">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                >
                  <p className="text-sm tracking-[0.3em] uppercase text-[#FFCBA4] mb-4">Start Your Journey</p>
                  <h2 className="font-serif text-3xl md:text-5xl text-[#F5F2EB] mb-6">
                    Ready to Experience Excellence?
                  </h2>
                  <p className="text-[#A3988F] mb-8">
                    Connect with our experts and discover how ARTERYVAIL can transform your lifestyle.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-4 justify-center">
                    <Link to="/nutrition" className="btn-primary inline-flex items-center justify-center space-x-3" data-testid="cta-nutrition-btn">
                      <span>Explore Nutrition</span>
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                    <Link to="/fashion" className="btn-secondary inline-flex items-center justify-center space-x-3" data-testid="cta-fashion-btn">
                      <span>Shop Fashion</span>
                      <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default HomePage;
